import { Link, useLocation } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import React from "react";

import logo from "$/assets/logoWhite.png";
import AidIcon from "$/lib/icons/AidIcon";
import GestionIcon from "$/lib/icons/GestionIcon";
import HomeIcon from "$/lib/icons/HomeIcon";
import OptimisationIcon from "$/lib/icons/OptimisationIcon";
import ProfileIcon from "$/lib/icons/ProfileIcon";
import SettingsIcon from "$/lib/icons/SettingsIcon";
import TransactionIcon from "$/lib/icons/TransactionIcon";
import { useAuth } from "$/lib/providers/AuthProvider";
import type { PathRouteType } from "$/lib/types/routes.types";
import { ROUTES } from "$/lib/utils/constants/routes.constants";
import { Route } from "$/routes/_user-layout";
import { useProfileProvider } from "$/routes/_user-layout/profile/-features/providers/ProfileProvider";

import NavbarAnimatedText from "./NavbarAnimatedText";
import NavbarBackgroundIndicator from "./NavbarBackgroundIndicator";
import NavbarDropdownItem from "./NavbarDropdownItem";
import NavbarItemIcon from "./NavbarItemIcon";
import NavbarSettingsDropdownMenu from "./NavbarSettingsDropdownMenu";

type NavItem = {
  label: string;
  path?: PathRouteType;
  icon: React.FC;
  type?: "popover" | "link";
  content?: React.ReactNode;
  hasRedDot?: boolean;
  basePath?: string; // Add this for path matching
};

const Navbar = () => {
  const { user } = useAuth();
  const location = useLocation();

  const { profileCompletionPercentage } = useProfileProvider();

  const navItems: NavItem[] = useMemo(() => {
    return [
      { label: "Accueil", path: ROUTES.HOMEPAGE, icon: HomeIcon },
      {
        label: "Transaction",
        path: ROUTES.TRANSACTIONS,
        icon: TransactionIcon,
      },
      {
        label: "Gestion",
        path: ROUTES.MANAGEMENT_MYBUSINESS,
        icon: GestionIcon,
        basePath: "/management",
      },
      {
        label: "Optimisation",
        path: ROUTES.OPTIMIZATION,
        icon: OptimisationIcon,
      },
      {
        label: "Profil",
        path: ROUTES.PROFILE_GENERAL,
        icon: ProfileIcon,
        hasRedDot: profileCompletionPercentage < 100,
        basePath: "/profile",
      },
      {
        label: "Aide",
        path: ROUTES.HELP_VIDEO,
        icon: AidIcon,
        basePath: "/help",
      },
      {
        label: "Paramètres",
        icon: SettingsIcon,
        type: "popover",
        content: <NavbarSettingsDropdownMenu />,
      },
    ];
  }, [profileCompletionPercentage]);

  const [_previousActiveIndex, setPreviousActiveIndex] = useState<
    number | null
  >(null);

  const findActiveIndex = (currentPath: string) => {
    return navItems.findIndex((item) => {
      if (item.basePath) {
        return currentPath.startsWith(item.basePath);
      }
      return item.path === currentPath;
    });
  };

  const [activeIndex, setActiveIndex] = useState(() =>
    findActiveIndex(location.pathname),
  );

  const [hoverIndex, setHoverIndex] = useState<number | null>(null);

  useEffect(() => {
    const newActiveIndex = findActiveIndex(location.pathname);
    if (newActiveIndex !== -1) {
      setActiveIndex(newActiveIndex);
    }
  }, [location.pathname]);

  const midpoint = Math.ceil(navItems.length / 2);
  const isDisconnectedBankAccount = Boolean(
    user && !user.lastConnectedAccountId?.length,
  );

  const displayActiveIndex = hoverIndex !== null ? hoverIndex : activeIndex;

  return (
    <nav className="relative mx-auto flex w-full items-center gap-24 rounded-xl bg-navbar-linear-gradient px-5 py-1">
      <Link to={ROUTES.HOMEPAGE}>
        <img src={logo} alt="logo" width={80} />
      </Link>
      <div className="relative flex w-full gap-9">
        {navItems.map((item, index) => {
          const isTransactionsItem = item.path === ROUTES.TRANSACTIONS;

          const redDotOrOrangeOrNothing =
            user && isTransactionsItem && user.hasToCategorizeTransactions
              ? "bg-notif"
              : item.hasRedDot ||
                  (isTransactionsItem && isDisconnectedBankAccount)
                ? "bg-notif"
                : "";
          return (
            <React.Fragment key={index}>
              {index === midpoint && (
                <span className="mx-0 w-[2px] bg-customWhite opacity-60 lg:mx-10" />
              )}
              {item.type === "popover" ? (
                <NavbarDropdownItem
                  dropdownMenuContentClassName="relative top-1 h-min-content rounded-xl"
                  onMouseLeave={() => {
                    setHoverIndex((prev) => (prev === index ? null : prev));
                  }}
                  active={displayActiveIndex === index}
                  Icon={item.icon}
                  label={item.label}
                  setActiveIndex={() => {
                    setPreviousActiveIndex(index);
                    setHoverIndex(index);
                  }}
                >
                  {item.content}
                </NavbarDropdownItem>
              ) : (
                <Link
                  className="relative h-14 w-[14%] cursor-pointer text-white hover:text-gray-300"
                  onMouseEnter={() => setHoverIndex(index)}
                  onMouseLeave={() => setHoverIndex(null)}
                  onClick={() => setActiveIndex(index)}
                  to={item.path!}
                  data-onboarding={isTransactionsItem ? "transactions-link" : undefined}
                >
                  <NavbarItemIcon
                    active={displayActiveIndex === index}
                    dotColor={redDotOrOrangeOrNothing}
                  >
                    <item.icon />
                  </NavbarItemIcon>
                  {displayActiveIndex === index && (
                    <>
                      <NavbarBackgroundIndicator />
                      <NavbarAnimatedText label={item.label} />
                    </>
                  )}
                </Link>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </nav>
  );
};

export default Navbar;
